import { RetrieveDocumentGenerationJob } from '../api';

/**
 * Title used when a cap-table generation job is registered with the
 * AsyncProcessor. The async job shape carries no processing type, so this title
 * is the signal that distinguishes cap-table generation from other async work.
 * Every producer (the Generate drawer and the app-load polling loop) and the
 * in-progress UI must agree on this exact value.
 */
export const CAP_TABLE_GENERATION_JOB_TITLE = 'Generating Cap Table';

/**
 * Title used when a cap-table *reprocess* job is registered with the
 * AsyncProcessor. Reprocessing ingests the same long PDFs as a fresh generation,
 * so it must share the in-progress indicator and the long (6h) GC threshold.
 * Both this and {@link CAP_TABLE_GENERATION_JOB_TITLE} are matched wherever
 * cap-table async work is recognised.
 */
export const CAP_TABLE_REPROCESS_JOB_TITLE = 'Reprocessing Cap Table';

/** Titles that mark a job as cap-table async work (generation or reprocess). */
export const CAP_TABLE_ASYNC_JOB_TITLES: readonly string[] = [
  CAP_TABLE_GENERATION_JOB_TITLE,
  CAP_TABLE_REPROCESS_JOB_TITLE,
];

/**
 * Extract an actionable failure message from a generation job, falling back to
 * the provided generic text when the job carries no parseable error detail.
 *
 * The backend serialises a job's results as a JSON string whose `error` field
 * holds the failure reason (e.g. a long-PDF parse failure or a timeout from the
 * stuck-job watchdog). This surfaces that reason to the user instead of a flat
 * "job failed" message.
 */
export const getGenerationJobFailureMessage = (
  job: Pick<RetrieveDocumentGenerationJob, 'results'>,
  fallback: string
): string => {
  if (!job.results) {
    return fallback;
  }
  try {
    const parsed = JSON.parse(job.results) as { error?: unknown };
    return typeof parsed.error === 'string' && parsed.error ? parsed.error : fallback;
  } catch {
    return fallback;
  }
};

export default getGenerationJobFailureMessage;
